import { AppContentLayoutNav, NavbarType } from '@base/@layouts/enums'
import { injectionKeyIsVerticalNavHovered } from '@base/@layouts/symbols'
import { _setDirAttr } from '@base/@layouts/utils'
import { layoutConfig } from '@base/config'

// ℹ️ We should not import themeConfig here but in urgency we are doing it for now

export const namespaceConfig = (str: string) => {
  // 使用英文前缀而不是中文应用名称，避免cookie名称包含非ASCII字符
  return `hegui-${str}`
}

export function cookieRef<T>(key: string, defaultValue: T) {
  return useCookie<T>(
    namespaceConfig(key),
    {
      default: () => defaultValue,
      maxAge: Number(import.meta.env.AUTH_MAX_AGE) || 60 * 60 * 24 * 30,
      // 添加基本安全配置
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax',
      httpOnly: false,
    },
  )
}

export const useLayoutConfigStore = defineStore('layoutConfig', () => {
  const route = useRoute()

  // 👉 Navbar Type
  const navbarType = ref(layoutConfig.navbar.type)

  // 👉 Navbar Type
  const isNavbarBlurEnabled = cookieRef('isNavbarBlurEnabled', layoutConfig.navbar.navbarBlur)

  // 👉 Vertical Nav Collapsed
  const isVerticalNavCollapsed = cookieRef('isVerticalNavCollapsed', layoutConfig.verticalNav.isVerticalNavCollapsed)

  // 👉 App Content Width
  const appContentWidth = cookieRef('appContentWidth', layoutConfig.app.contentWidth)

  // 👉 App Content Layout Nav
  const appContentLayoutNav = ref(layoutConfig.app.contentLayoutNav)

  // 删除横向布局相关的 watch 逻辑
  // watch(appContentLayoutNav, (val) => {
  //   // If Navbar type is hidden while switching to horizontal nav => Reset it to sticky
  //   if (val === AppContentLayoutNav.Horizontal) {
  //     if (navbarType.value === NavbarType.Hidden)
  //       navbarType.value = NavbarType.Sticky
  //
  //     isVerticalNavCollapsed.value = false
  //   }
  // })

  // 👉 Horizontal Nav Type
  // 删除横向导航类型，因为我们只使用垂直布局
  // const horizontalNavType = ref(layoutConfig.horizontalNav.type)
  const horizontalNavType = ref('sticky')

  //  👉 Horizontal Nav Popover Offset
  // const horizontalNavPopoverOffset = ref(layoutConfig.horizontalNav.popoverOffset)
  const horizontalNavPopoverOffset = ref(4)

  // 👉 Footer Type
  const footerType = ref(layoutConfig.footer.type)

  // 👉 Misc
  const isLessThanOverlayNavBreakpoint = computed(() => useMediaQuery(`(max-width: ${layoutConfig.app.overlayNavFromBreakpoint}px)`).value)

  // 👉 Layout Classes
  const _layoutClasses = computed(() => {
    const { y: windowScrollY } = useWindowScroll()

    return [
      `layout-nav-type-${appContentLayoutNav.value}`,
      `layout-navbar-${navbarType.value}`,
      `layout-footer-${footerType.value}`,
      {
        'layout-vertical-nav-collapsed':
          isVerticalNavCollapsed.value
          && appContentLayoutNav.value === 'vertical'
          && !isLessThanOverlayNavBreakpoint.value,
      },
      // 删除横向导航相关的类名
      // { [`horizontal-nav-${horizontalNavType.value}`]: appContentLayoutNav.value === 'horizontal' },
      `layout-content-width-${appContentWidth.value}`,
      { 'layout-overlay-nav': isLessThanOverlayNavBreakpoint.value },
      { 'window-scrolled': unref(windowScrollY) },
      route.meta.layoutWrapperClasses ? route.meta.layoutWrapperClasses : null,
    ]
  })

  // 👉 RTL
  // const isAppRTL = ref(layoutConfig.app.isRTL)
  const isAppRTL = ref(false)

  watch(isAppRTL, (val) => {
    _setDirAttr(val ? 'rtl' : 'ltr')
  })

  // 👉 Is Vertical Nav Mini
  /*
    This function will return true if current state is mini. Mini state means vertical nav is:
      - Collapsed
      - Isn't hovered by mouse
      - nav is not less than overlay breakpoint (hence, isn't overlay menu)

    ℹ️ We are getting `isVerticalNavHovered` as param instead of via `inject` because
        we are using this in `VerticalNav.vue` component which provide it and I guess because
        same component is providing & injecting we are getting undefined error
  */
  const isVerticalNavMini = (isVerticalNavHovered: Ref<boolean> | null = null) => {
    const isVerticalNavHoveredLocal = isVerticalNavHovered || inject(injectionKeyIsVerticalNavHovered) || ref(false)

    return computed(() => isVerticalNavCollapsed.value && !isVerticalNavHoveredLocal.value && !isLessThanOverlayNavBreakpoint.value)
  }

  return {
    appContentWidth,
    appContentLayoutNav,
    navbarType,
    isNavbarBlurEnabled,
    isVerticalNavCollapsed,
    horizontalNavType,
    horizontalNavPopoverOffset,
    footerType,
    isLessThanOverlayNavBreakpoint,
    isAppRTL,
    _layoutClasses,
    isVerticalNavMini,
  }
})
